import { PRODUCT_FACTS } from '../content/productFacts';

/**
 * Site-wide structured data, built from the approved product facts
 * (src/content/productFacts.json) and emitted by Seo.tsx on every public page.
 *
 * The organization, website and software are single entities with stable
 * identifiers on the site's origin, so every page (and page-level markup such
 * as BlogPosting, Service or Product) can reference the same things:
 *   https://shifttracker.net/#organization, #website, #software
 * Descriptions are in the language of the page that carries them, so the
 * English pages no longer say Turkish things about the product. `inLanguage`
 * describes the entity it is on: the page's language on WebPage, the site's
 * languages on WebSite, the application's own languages on SoftwareApplication.
 * No prices here: offers live only on the pricing page.
 */

export type SchemaLang = 'tr' | 'en';

const SITE = PRODUCT_FACTS.site.replace(/\/+$/, '');

export const ORGANIZATION_ID = `${SITE}/#organization`;
export const WEBSITE_ID = `${SITE}/#website`;
export const SOFTWARE_ID = `${SITE}/#software`;

/** Language names for ContactPoint.availableLanguage. */
const LANGUAGE_NAMES: Record<string, string> = { tr: 'Turkish', en: 'English' };

export interface SchemaPage {
  url: string; // canonical URL of the page
  title: string;
  description: string;
}

/** The @graph of organization, website, software and this page, in `lang`. */
export function siteGraph(lang: SchemaLang, page: SchemaPage): object {
  const facts = PRODUCT_FACTS;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: facts.name,
        url: `${SITE}/`,
        logo: facts.logo,
        slogan: facts.slogan[lang],
        email: facts.contact.email,
        telephone: facts.contact.phone,
        areaServed: facts.market,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'sales',
          email: facts.contact.email,
          telephone: facts.contact.phone,
          areaServed: facts.market,
          availableLanguage: facts.websiteLanguages.map((code) => LANGUAGE_NAMES[code] ?? code),
        },
      },
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: facts.name,
        url: `${SITE}/`,
        inLanguage: facts.websiteLanguages,
        publisher: { '@id': ORGANIZATION_ID },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': SOFTWARE_ID,
        name: facts.name,
        url: `${SITE}/`,
        image: facts.image,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        inLanguage: facts.applicationLanguages,
        slogan: facts.slogan[lang],
        description: facts.description[lang],
        featureList: facts.claims.map((claim) => claim.wording[lang]),
        audience: { '@type': 'BusinessAudience', audienceType: facts.audience[lang] },
        areaServed: facts.market,
        publisher: { '@id': ORGANIZATION_ID },
      },
      {
        '@type': 'WebPage',
        '@id': `${page.url}#webpage`,
        url: page.url,
        name: page.title,
        description: page.description,
        inLanguage: lang,
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': SOFTWARE_ID },
        publisher: { '@id': ORGANIZATION_ID },
      },
    ],
  };
}
